import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeContextType {
  themeMode: ThemeMode;
  isDarkMode: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved && ['system', 'light', 'dark'].includes(saved)) {
      return saved as ThemeMode;
    }
    return 'system';
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light') return false;
    if (saved === 'dark') return true;
    if (saved === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true; // Default to dark mode
  });

  // Update theme when mode changes
  useEffect(() => {
    let newIsDarkMode: boolean;
    
    if (themeMode === 'system') {
      newIsDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    } else {
      newIsDarkMode = themeMode === 'dark';
    }
    
    setIsDarkMode(newIsDarkMode);
    localStorage.setItem('themeMode', themeMode);
    document.documentElement.setAttribute('data-theme', newIsDarkMode ? 'dark' : 'light');
    
    // Force a re-render by updating the body class
    document.body.className = newIsDarkMode ? 'dark-theme' : 'light-theme';
  }, [themeMode]);

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
  };

  const toggleTheme = () => {
    if (themeMode === 'system') {
      setThemeModeState('light');
    } else if (themeMode === 'light') {
      setThemeModeState('dark');
    } else {
      setThemeModeState('system');
    }
  };

  return (
    <ThemeContext.Provider value={{ themeMode, isDarkMode, setThemeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}; 